'use client';

import { motion, useReducedMotion } from 'motion/react';
import { useIsInView } from '../../hooks/useIsInView';
import { useRef, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { ArrowRight, Phone, Mail, Linkedin, Check, Loader2 } from 'lucide-react';
import { isFinnishPage, CONTACT_FI, CONTACT_EN, CAREERS_FI } from '../../lib/blogPaths';
import { getWpImageSrc } from '../../lib/wpMedia';
import { CAREERS_COPY } from '../../constants/careersTranslations';

const DEFAULT_CONTACTS = [
  {
    photoUrl: '/team/joonas-korgan.png',
    name: 'Joonas Korgan',
    title: 'Head of Sales',
    phone: '+358 40 568 4617',
    email: 'joonas@identio.fi',
    linkedinUrl: 'https://www.linkedin.com/in/joonaskorgan/',
  },
  {
    photoUrl: '/team/pasi-ronkainen.png',
    name: 'Pasi Ronkainen',
    title: 'Sales',
    phone: '+358 44 902 9259',
    email: 'pasi@identio.fi',
    linkedinUrl: 'https://www.linkedin.com/in/pasi-ronkainen-0b12642/',
  },
];

export function FinalCTASection({
  variant = 'simple',
  title,
  subtitle,
  /** For variant team/careers: override CTA title, body, button text, and link href (from ACF). */
  body: bodyProp,
  contactButtonText = 'Talk to Our Team',
  contactButtonUrl,
  contactPhotoUrl,
  contactName,
  contactTitle,
  /** Optional: array of { photoUrl, name, title, phone, email, linkedinUrl }. First is primary; all shown in compact card. */
  contacts = DEFAULT_CONTACTS,
  /** When false, only the form is shown (no white contact sidebar). Use on Contact page where contacts are above. */
  showContactSidebar = true,
  /** Optional: job title for rekry form subject (e.g. "Senior Software Engineer"). */
  jobTitle = 'Senior Software Engineer',
}) {
  const router = useRouter();
  const isFinnish = isFinnishPage(router.pathname);
  const primary = contacts[0];
  const primaryTitle = primary && (isFinnish ? (primary.titleFi ?? primary.title) : primary.title);
  const contactPhoto = contactPhotoUrl ?? primary?.photoUrl;
  const contactNameResolved = contactName ?? primary?.name;
  const contactTitleResolved = contactTitle ?? primaryTitle;
  const contactEmail = primary?.email ?? 'hello@identio.fi';
  const contactPhone = primary?.phone ?? '';
  const ref = useRef(null);
  const reducedMotion = useReducedMotion();
  const isInView = useIsInView(ref, { once: true, margin: variant === 'simple' ? '-100px' : '-80px' });
  const motionDuration = reducedMotion ? 0 : 0.6;
  const [formState, setFormState] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    message: '',
  });
  const [rekryFormState, setRekryFormState] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    links: '',
    workInBothLanguages: false,
    message: '',
  });
  const [cvFiles, setCvFiles] = useState([]);
  const [contactFormSent, setContactFormSent] = useState(false);
  const [rekryFormSent, setRekryFormSent] = useState(false);
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [rekrySubmitting, setRekrySubmitting] = useState(false);
  const [contactError, setContactError] = useState(null);
  const [rekryError, setRekryError] = useState(null);

  const scrollToContact = () => {
    const el = document.getElementById('final-cta');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  /** Submit contact form via API; show confirmation or error. */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setContactError(null);
    setContactSubmitting(true);
    const pageContext = typeof window !== 'undefined' ? window.location.pathname : router.pathname || '';
    try {
      const res = await fetch('/api/send-form', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'contact',
          name: formState.name,
          company: formState.company,
          email: formState.email,
          phone: formState.phone,
          message: formState.message,
          pageContext,
          primaryContactEmail: contactEmail,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setContactError(data.error || 'Lähetys epäonnistui. Yritä uudelleen.');
        return;
      }
      setContactFormSent(true);
      // Fire Google Ads conversion event on successful contact form submit
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'conversion', {
          send_to: 'AW-664154700/wWKkCOjZpe4DEMzk2LwC',
        });
      }
    } catch (err) {
      setContactError(isFinnish ? 'Verkkovirhe. Yritä uudelleen.' : 'Network error. Please try again.');
    } finally {
      setContactSubmitting(false);
    }
  };

  const readFileAsBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = (reader.result || '').split(',')[1];
        resolve(base64 || '');
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  /** Submit rekry form via API with optional CV attachments; show confirmation or error. */
  const handleRekrySubmit = async (e) => {
    e.preventDefault();
    setRekryError(null);
    setRekrySubmitting(true);
    try {
      const attachments = await Promise.all(
        cvFiles.map(async (file) => ({
          filename: file.name,
          content: await readFileAsBase64(file),
        }))
      );
      const res = await fetch('/api/send-form', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'rekry',
          jobTitle,
          firstName: rekryFormState.firstName,
          lastName: rekryFormState.lastName,
          email: rekryFormState.email,
          phone: rekryFormState.phone,
          links: rekryFormState.links,
          workInBothLanguages: rekryFormState.workInBothLanguages,
          message: rekryFormState.message,
          attachments,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setRekryError(data.error || 'Lähetys epäonnistui. Yritä uudelleen.');
        return;
      }
      setRekryFormSent(true);
      // Fire Google Ads conversion event on successful rekry form submit
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'conversion', {
          send_to: 'AW-664154700/wWKkCOjZpe4DEMzk2LwC',
        });
      }
    } catch (err) {
      setRekryError('Verkkovirhe. Yritä uudelleen.');
    } finally {
      setRekrySubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormState((s) => ({ ...s, [e.target.name]: e.target.value }));
  };

  const handleRekryChange = (e) => {
    const target = e.target;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    setRekryFormState((s) => ({ ...s, [target.name]: value }));
  };

  const isSimple = variant === 'simple';
  const isCareers = variant === 'careers';
  const isTeam = variant === 'team';
  const isRekry = variant === 'rekry';
  const sectionClass = 'section-rhythm bg-navy text-white relative overflow-hidden';

  /** Shared layout for no-form CTAs (team, careers, simple) – same as AboutCTASection. */
  const ctaNoFormWrapper = 'max-w-4xl mx-auto px-6 py-32 text-center relative z-10';
  const ctaNoFormHeading = 'text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6';
  const ctaNoFormBody = 'text-xl md:text-2xl text-gray-300 mb-12 leading-relaxed max-w-3xl mx-auto';
  const ctaNoFormButton =
    'inline-flex items-center gap-2 bg-white text-navy hover:bg-gray-100 shadow-2xl text-lg px-10 py-5 rounded-xl font-medium transition-all duration-200 group hover:gap-3 touch-manipulation';

  /** CTA heading and subtitle for services pages (Palvelut, Ohjelmistokehitys, UI/UX-suunnittelu) when variant is withForm. */
  const servicesCtaCopy = {
    en: {
      title: 'Ready to accelerate your development?',
      subtitle: "Let's discuss how our AI-enhanced developers—individuals and teams—can integrate with yours.",
    },
    fi: {
      title: 'Aloitetaan yhteistyö',
      subtitle: 'Kerro tarpeistasi, niin suunnitellaan yhdessä, miten tiimimme tukee parhaiten projektisi menestystä.',
    },
  };
  const servicesCta = isFinnish ? servicesCtaCopy.fi : servicesCtaCopy.en;

  const formCopy = {
    formTitle: isFinnish ? 'Lähetä viesti' : 'Send a message',
    name: isFinnish ? 'Nimi' : 'Name',
    company: isFinnish ? 'Yritys' : 'Company',
    email: isFinnish ? 'Sähköposti' : 'Email',
    phone: isFinnish ? 'Puhelin' : 'Phone',
    message: isFinnish ? 'Viesti' : 'Message',
    sendButton: isFinnish ? 'Lähetä viesti' : 'Send Message',
    privacyIntro: isFinnish ? 'Lähettämällä hyväksyt ' : 'By submitting, you agree to our ',
    privacyLink: isFinnish ? 'Tietosuojakäytäntö' : 'Privacy Policy',
    privacyHref: isFinnish ? '/tietosuojaseloste' : '/privacy-policy',
    contactLabel: isFinnish ? 'Yhteystiedot' : 'Contact',
    teamHeading: isFinnish ? 'Haluatko mukaan joukkoomme?' : 'Want to join us?',
    teamBody: isFinnish
      ? 'Katso avoimet työpaikat ja mitä tarjoamme—omistajuus, joustavuus ja mahtavat kollegat.'
      : 'See open positions and what we offer—ownership, flexibility, and great people.',
    teamButton: isFinnish ? 'Katso avoimet työpaikat' : 'See open positions',
    simpleHeading: isFinnish
      ? 'Kiinnostuitko yhteistyöstä?'
      : 'Ready to build something exceptional?',
    simpleBody: isFinnish
      ? 'Kerromme mielellämme lisää tekemästämme työstä.'
      : "Whether you need one expert or an entire team, we're here to help. No pressure, no obligation—just a friendly chat about your goals.",
    simpleButton: isFinnish ? 'Ota yhteyttä tiimiin' : 'Talk to Our Team',
    simpleHref: isFinnish ? CONTACT_FI : CONTACT_EN,
    confirmationMessage: isFinnish
      ? 'Kiitos! Viestisi on lähetetty. Otamme yhteyttä mahdollisimman pian.'
      : 'Thank you! Your message has been sent. We’ll get back to you soon.',
  };

  const rekryFormCopy = {
    formTitle: 'Hakemuslomake',
    firstName: 'Etunimi',
    lastName: 'Sukunimi',
    email: 'Sähköposti',
    phone: 'Puhelinnumero',
    links: 'LinkedIn, GitHub, verkkosivut tms.',
    checkbox: 'Voin työskennellä sekä suomeksi että englanniksi',
    cvLabel: 'CV tai muut dokumentit (pdf, docx)',
    chooseFile: 'Valitse tiedostoja',
    noFile: 'Ei valittuja tiedostoja',
    message: 'Terveisesi meille',
    privacyIntro: 'Painamalla Lähetä hyväksyt ',
    privacyLink: 'tietosuojaselosteemme.',
    sendButton: 'Lähetä',
    contactLabel: 'Rekry',
    confirmationMessage: 'Kiitos! Hakemuksesi on lähetetty. Otamme yhteyttä mahdollisimman pian.',
  };

  const inputClass =
    'dark-form-input w-full px-4 py-3.5 rounded-lg text-base min-h-[48px] bg-white/[0.08] border border-white/20 text-white placeholder-white/50 focus:border-white/50 focus:outline-none transition-colors duration-200 [&:-webkit-autofill]:!bg-white/[0.08] [&:-webkit-autofill]:!text-white [&:-webkit-autofill]:shadow-[0_0_0_1000px_rgba(255,255,255,0.08)_inset]';
  const textareaClass =
    'dark-form-input w-full px-4 py-3.5 rounded-lg text-base bg-white/[0.08] border border-white/20 text-white placeholder-white/50 focus:border-white/50 focus:outline-none resize-none transition-colors duration-200 min-h-[120px]';

  return (
    <section id="final-cta" ref={ref} className={sectionClass}>
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-white rounded-full blur-3xl" />
        {(isSimple || isCareers || isTeam || isRekry) && (
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-white rounded-full blur-3xl" />
        )}
      </div>

      {isTeam ? (
        <div className={ctaNoFormWrapper}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: motionDuration }}
          >
            <h2 className={ctaNoFormHeading}>{formCopy.teamHeading}</h2>
            <p className={ctaNoFormBody}>{formCopy.teamBody}</p>
            <Link href={isFinnish ? `${CAREERS_FI}#open-positions` : '/careers#open-positions'} className={ctaNoFormButton}>
              {formCopy.teamButton}
              <ArrowRight className="w-5 h-5 shrink-0 transition-transform duration-200 group-hover:translate-x-1" aria-hidden />
            </Link>
          </motion.div>
        </div>
      ) : isCareers ? (
        (() => {
          const ctaCopy = isFinnish ? CAREERS_COPY.fi.finalCta : CAREERS_COPY.en.finalCta;
          const ctaTitleResolved = title ?? ctaCopy.title;
          const ctaBodyResolved = bodyProp ?? ctaCopy.body;
          const ctaButtonResolved = contactButtonText ?? ctaCopy.button;
          const ctaHref = contactButtonUrl ?? (isFinnish ? (ctaCopy.href ?? CAREERS_FI) : (ctaCopy.href ?? '/team')) ?? '/';
          return (
            <div className={ctaNoFormWrapper}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: motionDuration }}
              >
                <h2 className={ctaNoFormHeading}>{ctaTitleResolved}</h2>
                <p className={ctaNoFormBody}>{ctaBodyResolved}</p>
                <Link href={ctaHref} className={ctaNoFormButton}>
                  {ctaButtonResolved}
                  <ArrowRight className="w-5 h-5 shrink-0 transition-transform duration-200 group-hover:translate-x-1" aria-hidden />
                </Link>
              </motion.div>
            </div>
          );
        })()
      ) : isSimple ? (
        <div className={ctaNoFormWrapper}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: motionDuration }}
          >
            <h2 className={ctaNoFormHeading}>{formCopy.simpleHeading}</h2>
            <p className={ctaNoFormBody}>{formCopy.simpleBody}</p>
            <Link href={formCopy.simpleHref} className={ctaNoFormButton}>
              {formCopy.simpleButton}
              <ArrowRight className="w-5 h-5 shrink-0 transition-transform duration-200 group-hover:translate-x-1" aria-hidden />
            </Link>
          </motion.div>
        </div>
      ) : (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10 min-w-0">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: motionDuration }}
            className="text-center mb-16"
          >
            <h2 className="heading-2 text-white mb-4 font-semibold">
              {title ?? servicesCta.title}
            </h2>
            <p className="text-lg max-w-2xl mx-auto text-gray-200">
              {subtitle ?? servicesCta.subtitle}
            </p>
          </motion.div>

          <div className={`grid gap-10 lg:gap-12 items-start min-w-0 ${showContactSidebar || (isRekry && contacts?.length > 0) ? 'grid-cols-1 lg:grid-cols-[1fr_minmax(0,320px)]' : 'grid-cols-1 max-w-2xl mx-auto w-full'}`}>
            {/* Contact form or Rekry form */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: motionDuration, delay: reducedMotion ? 0 : 0.1 }}
              className={`bg-white/5 backdrop-blur rounded-2xl border border-white/10 p-6 sm:p-8 min-w-0 ${(showContactSidebar || (isRekry && contacts?.length > 0)) ? 'order-2 lg:order-1' : ''}`}
            >
              {isRekry ? (
                <>
                  <h3 className="text-xl font-semibold text-white mb-6">{rekryFormCopy.formTitle}</h3>
                  {rekryFormSent ? (
                    <div className="rounded-xl border border-white/20 bg-white/10 p-6 text-center">
                      <p className="text-white font-medium mb-0">{rekryFormCopy.confirmationMessage}</p>
                    </div>
                  ) : (
                  <form onSubmit={handleRekrySubmit} className="space-y-4">
                    {rekryError && (
                      <p className="text-red-300 text-sm bg-red-500/20 border border-red-400/30 rounded-lg px-4 py-2" role="alert">
                        {rekryError}
                      </p>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="rekry-form-firstName" className="block text-sm font-medium text-white/90 mb-1 sr-only">
                          {rekryFormCopy.firstName} *
                        </label>
                        <input
                          id="rekry-form-firstName"
                          type="text"
                          name="firstName"
                          placeholder={`${rekryFormCopy.firstName} *`}
                          value={rekryFormState.firstName}
                          onChange={handleRekryChange}
                          className={inputClass}
                          required
                        />
                      </div>
                      <div>
                        <label htmlFor="rekry-form-lastName" className="block text-sm font-medium text-white/90 mb-1 sr-only">
                          {rekryFormCopy.lastName} *
                        </label>
                        <input
                          id="rekry-form-lastName"
                          type="text"
                          name="lastName"
                          placeholder={`${rekryFormCopy.lastName} *`}
                          value={rekryFormState.lastName}
                          onChange={handleRekryChange}
                          className={inputClass}
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="rekry-form-email" className="block text-sm font-medium text-white/90 mb-1 sr-only">
                        {rekryFormCopy.email} *
                      </label>
                      <input
                        id="rekry-form-email"
                        type="email"
                        name="email"
                        placeholder={`${rekryFormCopy.email} *`}
                        value={rekryFormState.email}
                        onChange={handleRekryChange}
                        className={inputClass}
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="rekry-form-phone" className="block text-sm font-medium text-white/90 mb-1 sr-only">
                        {rekryFormCopy.phone}
                      </label>
                      <input
                        id="rekry-form-phone"
                        type="tel"
                        name="phone"
                        placeholder={rekryFormCopy.phone}
                        value={rekryFormState.phone}
                        onChange={handleRekryChange}
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <label htmlFor="rekry-form-links" className="block text-sm font-medium text-white/90 mb-1 sr-only">
                        {rekryFormCopy.links}
                      </label>
                      <input
                        id="rekry-form-links"
                        type="text"
                        name="links"
                        placeholder={rekryFormCopy.links}
                        value={rekryFormState.links}
                        onChange={handleRekryChange}
                        className={inputClass}
                      />
                    </div>
                    <label className="block">
                      <span className="sr-only">{rekryFormCopy.checkbox}</span>
                      <button
                        type="button"
                        role="checkbox"
                        aria-checked={rekryFormState.workInBothLanguages}
                        onClick={() => setRekryFormState((s) => ({ ...s, workInBothLanguages: !s.workInBothLanguages }))}
                        className={`w-full flex items-center gap-3 p-4 rounded-xl border-2 text-left transition-all duration-200 touch-manipulation ${
                          rekryFormState.workInBothLanguages
                            ? 'border-highlight bg-highlight/10 text-white'
                            : 'border-white/20 bg-white/5 text-white/80 hover:border-white/30 hover:bg-white/[0.08]'
                        }`}
                      >
                        <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md border-2 transition-colors ${rekryFormState.workInBothLanguages ? 'border-highlight bg-highlight text-navy' : 'border-white/40 bg-transparent'}`}>
                          {rekryFormState.workInBothLanguages && <Check className="h-4 w-4" strokeWidth={2.5} />}
                        </span>
                        <span className="text-sm font-medium">{rekryFormCopy.checkbox}</span>
                      </button>
                    </label>
                    <div>
                      <label className="block text-sm font-medium text-white/90 mb-1">
                        {rekryFormCopy.cvLabel}
                      </label>
                      <div className="flex flex-col gap-2">
                        <label className="cursor-pointer inline-flex items-center self-start px-4 py-2.5 rounded-lg border border-white/20 bg-white/10 text-white/90 text-sm font-medium hover:bg-white/[0.12] transition-colors touch-manipulation">
                          {rekryFormCopy.chooseFile}
                          <input
                            type="file"
                            accept=".pdf,.doc,.docx"
                            multiple
                            onChange={(e) => setCvFiles(Array.from(e.target.files ?? []))}
                            className="sr-only"
                          />
                        </label>
                        {cvFiles.length > 0 ? (
                          <ul className="text-sm text-white/80 space-y-1">
                            {cvFiles.map((f, i) => (
                              <li key={i} className="flex items-center gap-2">
                                <span className="text-highlight">•</span>
                                {f.name}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <span className="text-sm text-white/50">{rekryFormCopy.noFile}</span>
                        )}
                      </div>
                    </div>
                    <div>
                      <label htmlFor="rekry-form-message" className="block text-sm font-medium text-white/90 mb-1 sr-only">
                        {rekryFormCopy.message}
                      </label>
                      <textarea
                        id="rekry-form-message"
                        name="message"
                        placeholder={rekryFormCopy.message}
                        value={rekryFormState.message}
                        onChange={handleRekryChange}
                        rows={4}
                        className={textareaClass}
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={rekrySubmitting}
                      className="w-full bg-brand hover:bg-brand-hover disabled:opacity-60 disabled:cursor-not-allowed text-white font-medium py-3 px-6 rounded-xl transition-colors flex items-center justify-center gap-2"
                    >
                      {rekrySubmitting ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin shrink-0" aria-hidden />
                          {isFinnish ? 'Lähetetään…' : 'Sending…'}
                        </>
                      ) : (
                        <>
                          {rekryFormCopy.sendButton}
                          <ArrowRight className="w-5 h-5 shrink-0" aria-hidden />
                        </>
                      )}
                    </button>
                    <p className="text-xs text-gray-400">
                      {rekryFormCopy.privacyIntro}
                      <a href="/tietosuojaseloste" className="underline hover:text-white">
                        {rekryFormCopy.privacyLink}
                      </a>
                    </p>
                  </form>
                  )}
                </>
              ) : (
                <>
                  <h3 className="text-xl font-semibold text-white mb-6">{formCopy.formTitle}</h3>
                  {contactFormSent ? (
                    <div className="rounded-xl border border-white/20 bg-white/10 p-6 text-center">
                      <p className="text-white font-medium mb-0">{formCopy.confirmationMessage}</p>
                    </div>
                  ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    {contactError && (
                      <p className="text-red-300 text-sm bg-red-500/20 border border-red-400/30 rounded-lg px-4 py-2" role="alert">
                        {contactError}
                      </p>
                    )}
                    <div>
                      <label htmlFor="contact-form-name" className="block text-sm font-medium text-white/90 mb-1 sr-only">
                        {formCopy.name}
                      </label>
                      <input
                        id="contact-form-name"
                        type="text"
                        name="name"
                        placeholder={formCopy.name}
                        value={formState.name}
                        onChange={handleChange}
                        className={inputClass}
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-form-company" className="block text-sm font-medium text-white/90 mb-1 sr-only">
                        {formCopy.company}
                      </label>
                      <input
                        id="contact-form-company"
                        type="text"
                        name="company"
                        placeholder={formCopy.company}
                        value={formState.company}
                        onChange={handleChange}
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-form-email" className="block text-sm font-medium text-white/90 mb-1 sr-only">
                        {formCopy.email}
                      </label>
                      <input
                        id="contact-form-email"
                        type="email"
                        name="email"
                        placeholder={formCopy.email}
                        value={formState.email}
                        onChange={handleChange}
                        className={inputClass}
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-form-phone" className="block text-sm font-medium text-white/90 mb-1 sr-only">
                        {formCopy.phone}
                      </label>
                      <input
                        id="contact-form-phone"
                        type="tel"
                        name="phone"
                        placeholder={formCopy.phone}
                        value={formState.phone}
                        onChange={handleChange}
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-form-message" className="block text-sm font-medium text-white/90 mb-1 sr-only">
                        {formCopy.message}
                      </label>
                      <textarea
                        id="contact-form-message"
                        name="message"
                        placeholder={formCopy.message}
                        value={formState.message}
                        onChange={handleChange}
                        rows={4}
                        className={textareaClass}
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={contactSubmitting}
                      className="w-full bg-brand hover:bg-brand-hover disabled:opacity-60 disabled:cursor-not-allowed text-white font-medium py-3 px-6 rounded-xl transition-colors flex items-center justify-center gap-2"
                    >
                      {contactSubmitting ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin shrink-0" aria-hidden />
                          {isFinnish ? 'Lähetetään…' : 'Sending…'}
                        </>
                      ) : (
                        <>
                          {formCopy.sendButton}
                          <ArrowRight className="w-5 h-5 shrink-0" aria-hidden />
                        </>
                      )}
                    </button>
                    <p className="text-xs text-gray-400">
                      {formCopy.privacyIntro}
                      <a href={formCopy.privacyHref || '/tietosuojaseloste'} className="underline hover:text-white">
                        {formCopy.privacyLink}
                      </a>
                    </p>
                  </form>
                  )}
                </>
              )}
            </motion.div>

            {/* Right: compact contact card(s) – shown for contact form (showContactSidebar) or rekry (contacts passed) */}
            {(showContactSidebar || (isRekry && contacts?.length > 0)) && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: motionDuration, delay: reducedMotion ? 0 : 0.2 }}
              className="bg-white rounded-2xl border border-white/10 shadow-xl p-6 order-1 lg:order-2 lg:sticky lg:top-24 min-w-0"
            >
              <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">
                {isRekry ? rekryFormCopy.contactLabel : formCopy.contactLabel}
              </p>
              <div className="flex flex-col gap-0">
                {contacts.map((c, i) => {
                  const displayTitle = isFinnish ? (c.titleFi ?? c.title) : c.title;
                  return (
                  <div
                    key={c.email}
                    className={`flex items-start gap-4 ${i > 0 ? 'border-t border-gray-100 pt-5 mt-3' : 'pt-4'} pb-3`}
                  >
                    <Image
                      src={getWpImageSrc(c.photoUrl) || c.photoUrl}
                      alt={c.name}
                      width={56}
                      height={56}
                      className="rounded-lg object-cover border border-gray-100 shrink-0"
                      unoptimized
                    />
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-gray-900 leading-tight mb-0">{c.name}</p>
                      <p className="text-sm text-gray-500 leading-tight mt-0.5 mb-0">{displayTitle}</p>
                      <div className="mt-3 flex flex-col gap-1.5">
                        <a
                          href={`mailto:${c.email}`}
                          className="flex items-center gap-1.5 text-sm text-brand hover:underline"
                        >
                          <Mail className="w-4 h-4 shrink-0" />
                          {c.email}
                        </a>
                        {c.phone && (
                          <a
                            href={`tel:${c.phone.replace(/\s/g, '')}`}
                            className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-navy"
                          >
                            <Phone className="w-4 h-4 shrink-0" />
                            {c.phone}
                          </a>
                        )}
                        {c.linkedinUrl && (
                          <a
                            href={c.linkedinUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-[#0A66C2]"
                            aria-label={`${c.name} on LinkedIn`}
                          >
                            <Linkedin className="w-4 h-4 shrink-0" />
                            LinkedIn
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                );
                })}
              </div>
            </motion.div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

const contactShape = PropTypes.shape({
  photoUrl: PropTypes.string,
  name: PropTypes.string,
  title: PropTypes.string,
  titleFi: PropTypes.string,
  phone: PropTypes.string,
  email: PropTypes.string,
  linkedinUrl: PropTypes.string,
});

FinalCTASection.propTypes = {
  variant: PropTypes.string,
  title: PropTypes.string,
  subtitle: PropTypes.node,
  contactPhotoUrl: PropTypes.string,
  contactName: PropTypes.string,
  contactTitle: PropTypes.string,
  contactButtonText: PropTypes.string,
  contacts: PropTypes.arrayOf(contactShape),
  showContactSidebar: PropTypes.bool,
};
